import { stringify } from "qs";

const logger = (message) => {
  if ("development" === process.env.NODE_ENV) {
    console.log(message);
  }
};

const tbkApi = {
  map: process.env.REACT_APP_API_MAP || null,
  getApiBase: () =>
    (process.env.REACT_APP_API_BASE || "/api/").replace(/\/$/, ""), // strip trailing slash
  logger: logger,
  prepare: function() {
    const me = this;
    if (!me.map) {
      me.map = new Promise((resolve, reject) => {
        me.request("/api/map")
          .then((map) => {
            me.map = {};

            map.forEach((point) => {
              me.map[point.function] = point;
              me[point.function] = (...data) => {
                const req = me.determineRequest(point, data);

                if (req) {
                  return me.request(req.endpoint, req.method, req.parameters);
                }
              };
            });

            resolve(map);
          })
          .catch((e) => {
            reject("Unable to obtain API map");
          });
      });
    }
    return Promise.resolve(me.map);
  },
  getUri: function(func, ...args) {
    if (this.map[func]) {
      const req = this.determineRequest(this.map[func], args);
      return req ? this.getApiBase() + req.endpoint : undefined;
    }
    return undefined;
  },
  determineRequest: (point, parms) => {
    const req = {
      method: point.method.toUpperCase(),
      endpoint: point.route,
      parameters: parms || [],
    };

    // One by one, replace any {parameters} in the endpoint with the next item in parameters
    // Note, this does not yet know how to handle optional parameters parameters.
    // See https://www.slimframework.com/docs/v3/objects/router.html#how-to-create-routes
    try {
      req.endpoint = req.endpoint.replace(/{[^}]+}/g, function() {
        if (req.parameters.length) {
          return req.parameters.shift();
        }
        throw new Error("Not enough parameters passed for " + req.endpoint);
      });
    } catch (e) {
      logger(e);
      return false;
    }

    // If there are any parameters left, then we un-array-ify $parameters ( just set it to the first member )
    // Note, there is a limitation here that we can never pass more than one argument ( apart from arguments
    // that go into the endpoint ) to an SDK method.
    req.parameters = req.parameters.length ? req.parameters.shift() : null;

    return req;
  },
  activeRequests: {},
  getLocations: function() {
    return this.request("/locations", "GET").then(function(locations) {
      var list = Array.isArray(locations)
        ? locations
        : Object.values(locations || {});

      return list.map(function(item) {
        var location =
          item &&
          typeof item === "object" &&
          !Array.isArray(item) &&
          typeof item.ID === "undefined"
            ? Object.values(item)[0]
            : item;

        location = location || {};

        return Object.assign({}, location, {
          ID: location.ID ? Number(location.ID) : null,
          promo_box_a: location.promo_box_a
            ? Number(location.promo_box_a)
            : null,
          promo_box_b: location.promo_box_b
            ? Number(location.promo_box_b)
            : null,
          promo_box_c: location.promo_box_c
            ? Number(location.promo_box_c)
            : null,
          promo_box_alert: location.promo_box_alert
            ? Number(location.promo_box_alert)
            : null,
          active_promotions: Array.isArray(location.active_promotions)
            ? location.active_promotions.map(Number).filter(function(id) {
                return id > 0;
              })
            : [],
          pending_box_a: location.pending_box_a
            ? Number(location.pending_box_a)
            : null,
          pending_box_b: location.pending_box_b
            ? Number(location.pending_box_b)
            : null,
          pending_box_c: location.pending_box_c
            ? Number(location.pending_box_c)
            : null,
          pending_box_alert: location.pending_box_alert
            ? Number(location.pending_box_alert)
            : null,
          pending_promotions: Array.isArray(location.pending_promotions)
            ? location.pending_promotions.map(Number).filter(function(id) {
                return id > 0;
              })
            : [],
        });
      });
    });
  },

  shouldSendJson: function(endpoint, method) {
    const m = (method || "GET").toUpperCase();

    return (
      m === "POST" &&
      ["/promotion", "/promotions/pending", "/promotions/publish"].includes(
        endpoint,
      )
    );
  },
  savePendingPromotions(locationsPayload = {}) {
    return this.request("/promotions/pending", "POST", {
      locations: locationsPayload,
    });
  },
  publishPendingToLive(locations = null) {
    if (locations && locations.length > 0) {
      if (locations.length === 1) {
        return this.request("/promotions/publish", "POST", {
          location: Number(locations[0].ID),
        });
      }

      return Promise.all(
        locations.map((loc) =>
          this.request("/promotions/publish", "POST", {
            location: Number(loc.ID),
          }),
        ),
      );
    }

    return this.request("/promotions/publish", "POST", {});
  },
  savePromotion(promo = {}) {
    const cleaned = { ...promo };

    delete cleaned.locations;
    delete cleaned.promo_box_a;
    delete cleaned.promo_box_b;
    delete cleaned.promo_box_c;
    delete cleaned.promo_box_alert;
    delete cleaned.active_promotions;
    delete cleaned.pending_box_a;
    delete cleaned.pending_box_b;
    delete cleaned.pending_box_c;
    delete cleaned.pending_box_alert;
    delete cleaned.pending_promotions;
    delete cleaned.last_updated;
    delete cleaned.last_updated_pending;

    return this.request("/promotion", "POST", cleaned);
  },
  request: function(endpoint, method, data) {
    method = (method || "GET").toUpperCase();

    let url = this.getApiBase() + endpoint;
    const abortKey = method + "/" + url;

    let args = {
      credentials: "include",
      cache: "no-cache",
      method,
      headers: {
        Accept: "application/json",
      },
    };

    const dataStringified = data ? stringify(data) : null;
    const now = new Date();
    const timestamp = parseFloat(
      now.getSeconds() + now.getMilliseconds() / 1000,
    );
    if (this.activeRequests[abortKey]) {
      if (this.activeRequests[abortKey].data === dataStringified) {
        // this request is already active with the exact same data.  Don't bother calling the API again.
        return new Promise((resolve, reject) => {});
      }
      if (this.activeRequests[abortKey].controller) {
        this.activeRequests[abortKey].controller.abort();
      }
    }
    this.activeRequests[abortKey] = {
      controller: window.AbortController ? new window.AbortController() : null,
      data: dataStringified,
      timestamp,
    };
    if (this.activeRequests[abortKey].controller) {
      args.signal = this.activeRequests[abortKey].controller.signal;
    }

    if (data) {
      if (["GET", "DELETE"].includes(method)) {
        url += "?" + dataStringified;
      } else if (this.shouldSendJson(endpoint, method)) {
        args.headers["Content-Type"] = "application/json";
        args.body = JSON.stringify(data);
      } else {
        args.body = this.toFormData(data);
      }
    }

    if (["GET", "PUT", "POST", "DELETE"].indexOf(method) === -1) {
      args.method = "POST";
      args.headers["X-HTTP-Method-Override"] = method;
    }

    return new Promise((resolve, reject) => {
      fetch(url, args)
        .then((response) => {
          if (this.activeRequests[abortKey].timestamp !== timestamp) {
            // in case this browser doesn't have AbortController, or if that doesn't work ( looking at you Safari )
            // then we check the timestamp if the response isn't from the current request, then we abort, doing nothing.
            return;
          }

          delete this.activeRequests[abortKey]; // successful, delete this so same request can run again at another time
          const type = response.headers.get("content-type")
            ? response.headers.get("content-type").split(";")[0]
            : "text/html";

          if (503 === response.status) {
            /*
					store.dispatch({
						type: APP_HEARTBEAT,
						ping: false
					});
					reject();*/
          }

          if ("application/json" === type) {
            response
              .json()
              .then((json) => {
                if (json.hasOwnProperty("data")) {
                  if (json.success) {
                    resolve(json.data);
                  } else {
                    reject(json.data);
                  }
                } else if (json.message) {
                  reject(json.message);
                }
              })
              .catch((e) => {
                logger(e);
                reject();
              });
          } else {
            reject(response);
          }
        })
        .catch((e) => {
          if ("The user aborted a request." !== e.message) {
            // Above exception is when we've cancelled the request ( because the user is typing and another
            // request has come in before the previous one has finished.  All other exceptions, throw again.
            throw e;
          }
        });
    });
  },
  // takes a {} object and returns a FormData object
  // thanks https://gist.github.com/ghinda/8442a57f22099bdb2e34
  toFormData: (object) => {
    const toFormData = (obj, form, namespace) => {
      let fd = form || new FormData();
      let formKey;

      for (let property in obj) {
        if (!obj.hasOwnProperty(property)) {
          continue;
        }

        const value = obj[property];

        if (namespace) {
          formKey = namespace + "[" + property + "]";
        } else {
          formKey = property;
        }

        if (value === undefined) {
          continue;
        }

        if (value instanceof Date) {
          fd.append(formKey, value.toISOString());
        } else if (value === null) {
          fd.append(formKey, "");
        } else if (typeof value === "object" && !(value instanceof File)) {
          toFormData(value, fd, formKey);
        } else {
          fd.append(formKey, value);
        }
      }

      return fd;
    };

    return object ? toFormData(object) : null;
  },
};

export default tbkApi;
